import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ChevronDown, LogOut, Settings as SettingsIcon, Menu } from "lucide-react";
import { useNavigate } from "react-router-dom";
import ThemeToggle from "@/components/ThemeToggle";

const Header = ({ title, subtitle, onMenuClick }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header
      className="sticky top-0 z-30 h-16 border-b border-zinc-800 bg-zinc-950 flex items-center justify-between px-4 sm:px-6"
      data-testid="app-header"
    >
      <div className="flex items-center gap-3 min-w-0">
        <button
          onClick={onMenuClick}
          data-testid="mobile-menu-btn"
          className="lg:hidden flex items-center justify-center h-9 w-9 rounded-xl border border-zinc-800 text-zinc-400 hover:text-teal-300 transition-colors duration-200 shrink-0"
          aria-label="Open menu"
        >
          <Menu className="h-5 w-5" />
        </button>
        <div className="min-w-0">
          <h1 className="font-heading text-base sm:text-lg font-bold tracking-tight text-zinc-100 truncate" data-testid="page-title">
            {title}
          </h1>
          {subtitle && <p className="text-xs text-zinc-500 font-mono truncate">{subtitle}</p>}
        </div>
      </div>

      <div className="flex items-center gap-3 sm:gap-4">
        <button
          onClick={() => navigate("/pricing")}
          data-testid="plan-status-badge"
          title="View pricing"
          className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl border transition-colors duration-200 ${
            user?.is_pro
              ? "border-teal-500/40 bg-teal-400/10 text-teal-300"
              : "border-zinc-800 text-zinc-400 hover:border-teal-500/40"
          }`}
        >
          <span className={`h-2 w-2 rounded-full ${user?.is_pro ? "bg-teal-300" : "bg-zinc-500"} animate-pulse`} />
          <span className="font-mono text-xs uppercase tracking-wider">
            {user?.is_pro ? "Pro Member" : "Free Plan"}
          </span>
        </button>

        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className="flex items-center gap-2 hover:bg-zinc-900 rounded-xl px-2 py-1 transition-colors duration-200"
              data-testid="header-menu-trigger"
            >
              {user?.picture ? (
                <img src={user.picture} alt="" className="h-8 w-8 rounded-xl object-cover border border-zinc-700" />
              ) : (
                <div className="h-8 w-8 rounded-xl bg-zinc-800 flex items-center justify-center text-teal-300 font-heading font-bold">
                  {user?.name?.[0]?.toUpperCase() || "?"}
                </div>
              )}
              <ChevronDown className="h-4 w-4 text-zinc-500" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-zinc-900 border-zinc-800 rounded-xl w-56">
            <DropdownMenuLabel className="font-mono text-xs text-zinc-500 truncate">
              {user?.email}
            </DropdownMenuLabel>
            <DropdownMenuSeparator className="bg-zinc-800" />
            <DropdownMenuItem
              onClick={() => navigate("/settings")}
              className="text-zinc-300 focus:bg-zinc-800 focus:text-teal-300 cursor-pointer rounded-xl"
              data-testid="header-settings-item"
            >
              <SettingsIcon className="h-4 w-4 mr-2" /> Settings
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={logout}
              className="text-zinc-300 focus:bg-zinc-800 focus:text-teal-300 cursor-pointer rounded-xl"
              data-testid="header-logout-item"
            >
              <LogOut className="h-4 w-4 mr-2" /> Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Header;
